import styled from '@emotion/styled'
import type { Video } from '@/api/videos/type'
import { Avatar, Button, Flex, Tag, Text, Title } from '@collable/ui'
import { parseCount, parseSeconds } from '@/utils/number'
import { hexWithOpacity } from '@/utils/color'
import dayjs from '@/config/dayjsConfig'
import Divider from '@/components/common/Divider'
import { isRegistered } from '@/utils/video'
import { Link } from 'react-router'
import pagePath from '@/utils/pagePath'

interface ManageVideoItemProps {
  video: Video
  contentsOnly?: boolean
}

const ManageVideoItem = ({ video, contentsOnly = false }: ManageVideoItemProps) => {
  const runningTime = parseSeconds(Number(video.duration))
  const isRegisteredVideo = isRegistered(video)

  return (
    <Container as="li" gap={28}>
      <ThumbnailContainer>
        <img src={video.thumbnailUrl} alt={video.title} />
        <Text size="body4" fontWeight="medium" color="commonWhite">
          {runningTime}
        </Text>
      </ThumbnailContainer>
      <Content direction="column" gap={12}>
        {isRegisteredVideo && (
          <Flex gap={8}>
            <Tag size="large" color="primary">
              유료광고 포함
            </Tag>
            <VideoInfo gap={6} alignItems="center">
              <Avatar size="s" type="sponsor" src={video.brandLogoUrl} />
              <Text size="body3" fontWeight="medium">
                {video.brandName}
              </Text>
            </VideoInfo>
          </Flex>
        )}
        <Flex direction="column" gap={4}>
          <Title size="subTitle2" fontWeight="bold" as="h3">
            {video.title}
          </Title>
          <Flex alignItems="center" className="countWrapper">
            <Text size="body3" color="labelNeutral">
              조회수
            </Text>
            <Text size="body3" color="primaryNormal" className="count">
              {parseCount(video.viewCount)}
            </Text>
            <Divider direction="vertical" width={12} margin="0 8px" />
            <Text size="body3" color="labelNeutral">
              좋아요
            </Text>
            <Text size="body3" color="labelNeutral" className="count">
              {parseCount(video.likeCount)}
            </Text>
            <Divider direction="vertical" width={12} margin="0 8px" />
            <Text size="body3" color="labelNeutral">
              댓글
            </Text>
            <Text size="body3" color="labelNeutral" className="count">
              {!!video.commentCount && parseCount(video.commentCount)}
              {!video.commentCount && '-'}
            </Text>
          </Flex>
        </Flex>
        <Text size="body3" color="labelAssistive">
          {dayjs(video.publishedAt).fromNow()}
        </Text>
      </Content>
      {!contentsOnly && (
        <Link to={pagePath.videoDetail(video.videoId)}>
          {isRegisteredVideo ? (
            <Button
              size="medium"
              color="neutral"
              kind="soft"
              variant="leftIcon"
              iconProps={{
                name: 'arrowReload',
              }}
            >
              수정하기
            </Button>
          ) : (
            <Button
              size="medium"
              kind="soft"
              color="primary"
              variant="rightIcon"
              iconProps={{
                name: 'plus',
              }}
            >
              광고 정보 등록하기
            </Button>
          )}
        </Link>
      )}
    </Container>
  )
}

export default ManageVideoItem

const Container = styled(Flex)`
  .count {
    margin-left: 4px;
  }

  a {
    display: contents;
    margin-left: auto;
  }
`

const ThumbnailContainer = styled.div`
  position: relative;
  min-width: 320px;
  height: 180px;
  border-radius: 20px;
  overflow: hidden;

  > img {
    width: 100%;
    height: 100%;
  }
  > p {
    position: absolute;
    right: 12px;
    bottom: 12px;
    padding: 4px 8px;
    border-radius: 8px;
    background-color: ${hexWithOpacity('commonBlack', 'opacity74')};
  }
`

const VideoInfo = styled(Flex)`
  padding: 0 10px;
  height: 100%;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.fill.fillAlternative};
`

const Content = styled(Flex)`
  flex: 1;
`
